/**
 * StudentLiveCheckpoint — Sam en live : la conversation d'un checkpoint (la main levée, le silence de
 * dix minutes), dans la feuille de Sam (`<SamSheet>` + `<CheckpointConversation>`).
 *
 * La conversation est au moteur (`useCheckpointConversation`, le même crochet que le starting
 * block) ; ici, ce que l'ancien `SamCheckpointOverlay` faisait autour d'elle :
 *  - l'attribut `ongoing_checkpoint` du participant, posé à l'ouverture et retiré à la fermeture
 *    (le tuteur voit que l'élève parle à Sam) ;
 *  - à chaque tour, `useLiveKitSync.handleConversationComplete` : l'attribut retiré au bout, la main
 *    levée pour le tuteur sur `tutor_needed` (`raise_hand_source: checkpoint`), et le tuteur prévenu
 *    (`agentConversationCompleted`) ;
 *  - l'activité que Sam propose (`todo_activity_launch`) : elle s'ouvre ;
 *  - la photo d'une réponse, prise comme une page de la copie : au navigateur, la caméra du nouvel
 *    espace (`StudentCopyCamera` → `<ActivityCopyCamera>`, avec le choix de la caméra et la piste de
 *    l'appel reprise en live) au lieu de l'ancien écran `camera` ; au téléphone, le scanner (Genius
 *    Scan) de l'appareil des ports (`useMediaPickerAdapter`).
 * L'ancien « Voir l'activité » n'est pas repris (Wajih, 19/10) : la feuille laisse voir le haut de
 * l'activité, et au grand écran l'activité est à côté.
 *
 * Les sons de Sam en live, ceux de l'ancien (`SamConversationBase`) : son jingle à l'ouverture, un
 * clic à chaque réponse touchée, le pétale gagné (`PetalRewardBadge`), aux moments que la conversation
 * dit (`onSound`) — par `playLiveSound`, qui ménage la session audio de l'appel.
 *
 * **Le panneau se pose dès le toucher** (`live.checkpointOpening`) : tant que Sam prépare sa première
 * phrase, la forme de la conversation (`<CheckpointConversation loading>`), et la croix, qui abandonne
 * l'ouverture. Une ouverture qui échoue le dit dans le panneau. La feuille reste la même quand la
 * conversation arrive : elle n'entre qu'une fois.
 *
 * Le chat ouvert pendant la conversation passe devant : la page le pose après ce panneau, qui reste
 * ouvert dessous et se retrouve tel quel à la fermeture du chat.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Platform } from "react-native"

import { formIds } from "../../../../../../data/constants/formConstants"
import { CheckpointConversation, SamSheet, type SamSheetDetent } from "../../../../../../design-system/student"
import { type ApiCheckpointConversation } from "../../../../../../engine/precall/checkpoint/checkpointConversationMachine"
import { launchedTodoId } from "../../../../../../engine/precall/checkpoint/generatedActivity"
import { useCheckpointConversation } from "../../../../../../engine/precall/checkpoint/useCheckpointConversation"
import { useLiveKitSync } from "../../../../../../engine/precall/checkpoint/useLiveKitSync"
import { playLiveSound } from "../../../../../../engine/live/student/playLiveSound"
import { type StudentLive } from "../../../../../../engine/live/student/useStudentLive"
import { startingBlockViewModel } from "../../../../view-models/student/LiveEntry/startingBlockViewModel"
import { useMediaPickerAdapter } from "../../../hooks/useMediaPickerAdapter"
import { StudentCopyCamera } from "../Activity/StudentCopyCamera"
import { SAM_SOUNDS } from "../studentSounds"

/** En live, Sam renvoie à l'activité et au tuteur. */
const ERROR_IN_LIVE = "Oups, un souci de notre côté. Ferme et lève la main : ton masteur t'aide."

type Props = {
    live: StudentLive
    /** La conversation, ou `null` le temps que Sam prépare sa première phrase (`live.checkpointOpening`). */
    conversation: ApiCheckpointConversation | null
    activityId: number | null
    /** Sam propose une activité de la liste : l'écran l'ouvre. */
    onSuggestActivity: (activityId: number) => void
}

export function StudentLiveCheckpoint({ live, conversation, activityId, onSuggestActivity }: Props) {
    const [detent, setDetent] = useState<SamSheetDetent>("default")
    // La croix et le voile ferment la conversation ouverte ; avant qu'elle n'arrive, l'ouverture.
    const closeRef = useRef<(() => void) | null>(null)
    const close = useCallback(() => {
        if (closeRef.current != null) closeRef.current()
        else live.actions.dismissCheckpointOpening()
    }, [live.actions])

    return (
        <SamSheet onClose={close} detent={detent} onDetentChange={setDetent}>
            {conversation != null ? (
                <LiveCheckpointConversation
                    key={String(conversation.conversation?.id ?? "checkpoint")}
                    live={live}
                    conversation={conversation}
                    activityId={activityId}
                    onSuggestActivity={onSuggestActivity}
                    closeRef={closeRef}
                />
            ) : (
                <CheckpointConversation
                    days={[]}
                    loading={live.checkpointOpening === "opening"}
                    error={live.checkpointOpening === "failed"}
                    errorText={ERROR_IN_LIVE}
                    answered={0}
                    canClose
                    onClose={close}
                />
            )}
        </SamSheet>
    )
}

type ConversationProps = Omit<Props, "conversation"> & {
    conversation: ApiCheckpointConversation
    /** La fermeture de la conversation, que la feuille appelle au voile. */
    closeRef: React.MutableRefObject<(() => void) | null>
}

/** La conversation ouverte : le crochet du moteur, l'attribut du participant, l'appareil photo. */
function LiveCheckpointConversation({
    live,
    conversation,
    activityId,
    onSuggestActivity,
    closeRef,
}: ConversationProps) {
    const { user, liveSession } = live
    const sync = useLiveKitSync({
        room: live.room.room,
        setAttributesThrottled: live.setAttributes,
        triggerSamNotification: (type: string, data: Record<string, unknown>) => {
            void live.send(type, data, null, "keep")
        },
        user,
        currentLiveSession: liveSession,
    }) as {
        markCheckpointActive: () => void
        markCheckpointInactive: () => void
        handleConversationComplete: (data: unknown) => void
    }

    // L'attribut tient le temps de la feuille, comme `SamCheckpointOverlay`.
    const syncRef = useRef(sync)
    syncRef.current = sync
    useEffect(() => {
        syncRef.current.markCheckpointActive()
        return () => syncRef.current.markCheckpointInactive()
        // À l'ouverture de la feuille seulement.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const checkpoint = useCheckpointConversation({
        conversation,
        role: "student",
        surface: "checkpoint_overlay",
        activityId,
        onSound: (sound) => playLiveSound(SAM_SOUNDS[sound], live.telemetry),
        onTurn: (data) => {
            sync.handleConversationComplete(data)
            const suggested = launchedTodoId(data)
            if (suggested != null) onSuggestActivity(suggested)
        },
        onFinished: live.actions.closeCheckpoint,
        onClosed: live.actions.closeCheckpoint,
    })
    const vm = startingBlockViewModel(checkpoint.state)
    const [date, setDate] = useState("")
    closeRef.current = checkpoint.close
    useEffect(
        () => () => {
            closeRef.current = null
        },
        [closeRef]
    )

    // La photo d'une réponse se prend comme une page de la copie : au navigateur, la caméra du nouvel
    // espace, montée le temps d'une prise ; au téléphone, le scanner.
    const camera = useMediaPickerAdapter({ formId: formIds.CHECKPOINT, enableGallery: false, enableDocument: false })
    const [webCameraOpen, setWebCameraOpen] = useState(false)
    const awaitingPhotoRef = useRef(false)
    useEffect(() => {
        if (!checkpoint.photoRequested) return
        if (Platform.OS === "web") setWebCameraOpen(true)
        else {
            awaitingPhotoRef.current = true
            camera.launchCamera?.()
        }
        checkpoint.cancelGesture()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkpoint.photoRequested])
    useEffect(() => {
        const [photo] = camera.incomingCameraImages
        if (!awaitingPhotoRef.current || photo == null) return
        awaitingPhotoRef.current = false
        camera.onCameraImagesConsumed()
        checkpoint.sendPhoto(photo.uri)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [camera.incomingCameraImages])

    const textEntry = useMemo(
        () => (vm.writing ? { value: vm.textValue, onChange: checkpoint.setText, onSend: checkpoint.sendText } : null),
        [vm.writing, vm.textValue, checkpoint.setText, checkpoint.sendText]
    )

    return (
        <>
            <CheckpointConversation
                days={vm.days}
                thinking={vm.thinking}
                replies={vm.replies}
                onReply={checkpoint.reply}
                chosenReplyKey={vm.chosenReplyKey}
                textEntry={textEntry}
                dateEntry={
                    checkpoint.dateRequested
                        ? { value: date, onChange: setDate, onSend: () => checkpoint.sendDate(date) }
                        : null
                }
                answered={vm.answered}
                earned={vm.earned}
                canClose={checkpoint.canClose}
                onClose={checkpoint.close}
                ended={vm.ended}
                feedback={vm.feedback}
                onFeedback={(value) => checkpoint.giveFeedback(value === "positive" ? 1 : -1)}
                feedbackSubmitting={vm.feedbackSubmitting}
                error={vm.error}
                errorText={ERROR_IN_LIVE}
            />
            {webCameraOpen && (
                <StudentCopyCamera inLive onPhoto={checkpoint.sendPhoto} onClose={() => setWebCameraOpen(false)} />
            )}
        </>
    )
}
